import { useLayoutEffect, useRef, useState } from 'react'

import { EMPTY_OBJECT } from '../../constants/common'
import { isValueNumberType } from '../../utils/common'
import {
  isEventKeyArrowLeft,
  isEventKeyArrowRight,
  preventDefault,
} from '../../utils/events'
import { toCSSPixel } from '../../utils/styles'
import { useWidthResize } from '../useWidthResize'

import {
  USE_ELEMENT_WIDTH_RESIZE_KEYBOARD_STEP,
  USE_ELEMENT_WIDTH_RESIZE_KEYBOARD_STEP_LARGE,
} from './useElementWidthResize.constants'

export function useElementWidthResize({
  containerRef,
  disabled,
  getNextWidth,
  initialWidth,
  maxWidth,
  maxWidthRatioToContainer,
  minWidth,
  onResize,
  onResizeEnd,
  onResizeStart,
} = EMPTY_OBJECT) {
  const [width, setWidth] = useState(
    isValueNumberType(initialWidth) ? initialWidth : minWidth
  )
  const widthRef = useRef(width)
  const elementRef = useRef(null)

  useLayoutEffect(() => {
    const containerElement =
      containerRef?.current ?? elementRef.current?.parentElement

    if (
      !isValueNumberType(initialWidth) &&
      isValueNumberType(maxWidthRatioToContainer) &&
      containerElement
    ) {
      const nextWidth =
        containerElement.getBoundingClientRect().width *
        maxWidthRatioToContainer

      widthRef.current = nextWidth
      setWidth(nextWidth)
    }
  }, [])

  function handleResize(params) {
    widthRef.current = params.width
    setWidth(params.width)
    onResize?.(params)
  }

  function handleResizeEnd(params) {
    onResizeEnd?.({
      ...params,
      width: widthRef.current,
    })
  }

  const { getResolvedMaxWidth, getWidthByDelta, handleResizeStart } =
    useWidthResize({
      containerRef,
      disabled,
      getNextWidth,
      maxWidth,
      maxWidthRatioToContainer,
      minWidth,
      onResize: handleResize,
      onResizeEnd: handleResizeEnd,
      onResizeStart,
    })

  function handlePointerDown(event) {
    handleResizeStart({
      event,
      width,
    })
  }

  function handleKeyDown(event) {
    const isLeft = isEventKeyArrowLeft(event)
    const isRight = isEventKeyArrowRight(event)

    if (!disabled && (isLeft || isRight)) {
      preventDefault(event)

      const step = event.shiftKey
        ? USE_ELEMENT_WIDTH_RESIZE_KEYBOARD_STEP_LARGE
        : USE_ELEMENT_WIDTH_RESIZE_KEYBOARD_STEP
      const nextWidth = getWidthByDelta({
        deltaX: isLeft ? -step : step,
        startWidth: width,
      })

      if (nextWidth !== width) {
        handleResize({ event, width: nextWidth })
        handleResizeEnd({ event })
      }
    }
  }

  return {
    elementProps: {
      ref: elementRef,
      style: {
        maxWidth: toCSSPixel(maxWidth),
        minWidth: toCSSPixel(minWidth),
        width: toCSSPixel(width),
      },
    },
    resizeHandleProps: {
      'aria-orientation': 'vertical',
      'aria-valuemax': Number.isFinite(getResolvedMaxWidth(width))
        ? Math.round(getResolvedMaxWidth(width))
        : undefined,
      'aria-valuemin': minWidth,
      'aria-valuenow': Math.round(width),
      role: 'separator',
      style: {
        cursor: disabled ? 'default' : 'col-resize',
        touchAction: 'none',
        userSelect: 'none',
      },
      tabIndex: disabled ? -1 : 0,
      onKeyDown: handleKeyDown,
      onPointerDown: handlePointerDown,
    },
    setWidth,
    width,
  }
}
